import { isSuperAdmin } from '@/lib/roles';
import type { CurrentUserResponse } from '@/types/generated';
import { PUBLIC_UI_LANGUAGES, UiLanguage } from '@/types/generated/enums';

export const SUPPORTED_LANGUAGES: readonly UiLanguage[] =
  Object.values(UiLanguage);

// Launched languages, generated from the backend's: the browser infers only these, and only staff can pick any other.
export const PUBLIC_LANGUAGES: readonly UiLanguage[] = PUBLIC_UI_LANGUAGES;
export const DEFAULT_LANGUAGE: UiLanguage = UiLanguage.ENGLISH;

// A language's own name, in itself — never routed through `t()`, the same as `BRAND_NAME`.
// A language picker showing "Inglés" only to a Spanish-reading user is the wrong UX.
export const LANGUAGE_DISPLAY_NAMES: Record<UiLanguage, string> = {
  [UiLanguage.ENGLISH]: 'English',
  [UiLanguage.SPANISH]: 'Español',
  [UiLanguage.GREEK]: 'Ελληνικά',
};

// The backend may ship a language before this client has a bundle for it.
export const isUiLanguage = (
  value: string | null | undefined,
): value is UiLanguage =>
  SUPPORTED_LANGUAGES.some((language) => language === value);

export const fromBrowser = (
  tags: readonly string[],
  launched: readonly UiLanguage[] = PUBLIC_LANGUAGES,
): UiLanguage | undefined => {
  for (const tag of tags) {
    const primary = tag.toLowerCase().split('-')[0];
    const match = launched.find((language) => language === primary);
    if (match !== undefined) return match;
  }
  return undefined;
};

interface LanguageInputs {
  readerLanguage?: string | null;
  browserLanguages: readonly string[];
}

// The backend decides a signed-in reader's language, whichever organization the tab acts in.
export const resolveUiLanguage = ({
  readerLanguage,
  browserLanguages,
}: LanguageInputs): UiLanguage => {
  if (isUiLanguage(readerLanguage)) return readerLanguage;
  return fromBrowser(browserLanguages) ?? DEFAULT_LANGUAGE;
};

export const uiLanguageFor = (
  me: CurrentUserResponse | undefined,
): UiLanguage =>
  resolveUiLanguage({
    readerLanguage: me?.ui_language,
    browserLanguages:
      typeof navigator === 'undefined' ? [] : navigator.languages,
  });

// Keeps the language already set, so a non-staff picker never shows a blank choice.
export const pickableLanguages = (
  user: CurrentUserResponse,
  current: UiLanguage | null | undefined,
  launched: readonly UiLanguage[] = PUBLIC_LANGUAGES,
): readonly UiLanguage[] =>
  isSuperAdmin(user)
    ? SUPPORTED_LANGUAGES
    : SUPPORTED_LANGUAGES.filter(
        (language) => launched.includes(language) || language === current,
      );
